import React, { useState, useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import TemplateDataContext from "../../Contexts/TemplateData.context";
import { jsonKeyMapping } from "../../Config/Constants";
import { TemplateObject } from "../../Models/TemplateObject";

interface HorizontalTipsBarProps {
  title?: string;
  description?: string;
}

const HorizontalTipsBar: React.FC<HorizontalTipsBarProps> = ({
  title: propTitle,
  description: propDescription,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dynamicTitle, setDynamicTitle] = useState<string>("");
  const [dynamicDescription, setDynamicDescription] = useState<string>("");

  const { state } = useContext(TemplateDataContext);
  const { activeTemplateSteps } = state;
  const { stepName } = useParams();

  // Load dynamic content from current step (same logic as ExpandableSidebar)
  useEffect(() => {
    // If explicit props provided, respect them fully
    if (propTitle || propDescription) {
      setDynamicTitle(propTitle || "");
      setDynamicDescription(propDescription || "");
      return;
    }

    // Prefer body dataset if present (ReviewSummary sets these); this ensures review step text is authoritative
    const bodyTitle = (document.body.dataset || {}).tooltipDefaultTitle;
    const bodyDesc = (document.body.dataset || {}).tooltipDefaultDescription;
    if (bodyTitle || bodyDesc) {
      setDynamicTitle(bodyTitle || "");
      setDynamicDescription(bodyDesc || "");
      // Still allow normal step-based update IF not on review step
      if (stepName === "review-submit") return; // lock to ReviewSummary values
    }

    if (activeTemplateSteps.length > 0 && stepName) {
      const currentStep = activeTemplateSteps.find(
        (step: TemplateObject) => step[jsonKeyMapping.SLUGIFY] === stepName
      );
      if (currentStep) {
        // Skip overriding if this is review step & dataset already populated
        if (stepName === "review-submit" && (bodyTitle || bodyDesc)) return;
        const helpContent = extractHelpContent(currentStep);
        setDynamicTitle(helpContent.title);
        setDynamicDescription(helpContent.description);
      } else {
        setDynamicTitle("Help");
        setDynamicDescription("No help information available for this step.");
      }
    } else {
      setDynamicTitle("Template Help");
      setDynamicDescription(
        "Navigate through the template steps to see context-specific help."
      );
    }
  }, [activeTemplateSteps, stepName, propTitle, propDescription]);

  // Extract help information from current step (same logic as ExpandableSidebar)
  const extractHelpContent = (currentStep: TemplateObject) => {
    // First priority: Check if there are form fields with subtooltips (most specific help)
    if (
      currentStep[jsonKeyMapping.FORM_GROUP] &&
      currentStep[jsonKeyMapping.FORM_GROUP].length > 0
    ) {
      const allSubtooltips: Array<{ title: string; description: string }> = [];

      // Collect all subtooltips from all form fields in the step
      const collectSubtooltips = (fields: any[]) => {
        fields.forEach((field: any) => {
          if (field.subtooltip && field.subtooltip.trim()) {
            // Extract title from HTML (between <span class='headline'> tags)
            const titleMatch = field.subtooltip.match(
              /<span class=['"]headline['"]>(.*?)<\/span>/
            );
            const title = titleMatch ? titleMatch[1] : field.label || "Help";

            // Extract description (text after <br> tag, removing HTML)
            const descriptionMatch = field.subtooltip.match(/<br>(.*)/);
            let description = descriptionMatch
              ? descriptionMatch[1].replace(/<[^>]*>/g, "").trim()
              : field.subtooltip.replace(/<[^>]*>/g, "").trim();

            // If the description starts with the same text as the title, remove that duplication
            if (title && description.startsWith(title)) {
              description = description.substring(title.length).trim();
            }

            allSubtooltips.push({ title, description });
          }

          // Check nested fields (like morefields in radio buttons)
          if (field.inputRadio && Array.isArray(field.inputRadio)) {
            field.inputRadio.forEach((radioOption: any) => {
              if (
                radioOption.morefields &&
                Array.isArray(radioOption.morefields)
              ) {
                collectSubtooltips(radioOption.morefields);
              }
            });
          }

          // Check other nested field types
          if (field.morefields && Array.isArray(field.morefields)) {
            collectSubtooltips(field.morefields);
          }
        });
      };

      collectSubtooltips(currentStep[jsonKeyMapping.FORM_GROUP]);

      if (allSubtooltips.length > 0) {
        // If single subtooltip, use the headline as title
        if (allSubtooltips.length === 1) {
          return {
            title: allSubtooltips[0].title,
            description: allSubtooltips[0].description,
          };
        } else {
          // Multiple subtooltips: render each with headline styling + description styling
          const combinedDescription = allSubtooltips
            .map((item, index) => `**${item.title}**${item.description}`)
            .join("\n\n");

          return {
            title: "", // No main title, headlines will be in the description as bold
            description: combinedDescription,
          };
        }
      }

      // Second priority: Check for regular tooltips in form fields
      const fieldsWithTooltips = currentStep[jsonKeyMapping.FORM_GROUP].filter(
        (field: any) => field.tooltip && field.tooltip.trim()
      );

      if (fieldsWithTooltips.length > 0) {
        const field = fieldsWithTooltips[0];
        return {
          title: field.label || "Help",
          description: field.tooltip.replace(/<[^>]*>/g, "").trim(),
        };
      }
    }

    // Third priority: Check if there are FAQs for this step
    if (currentStep.faq && currentStep.faq.length > 0) {
      const firstFaq = currentStep.faq[0];
      return {
        title: firstFaq.header || "FAQ",
        description: (
          firstFaq.Text ||
          firstFaq.text ||
          "No FAQ content available."
        )
          .replace(/<[^>]*>/g, "")
          .trim(),
      };
    }

    // Fourth priority: Check step-level tooltip
    if (currentStep.tooltip && currentStep.tooltip.trim()) {
      return {
        title: currentStep[jsonKeyMapping.SLUG] || "Help",
        description: currentStep.tooltip.replace(/<[^>]*>/g, "").trim(),
      };
    }

    // Fifth priority: Step description
    if (
      currentStep[jsonKeyMapping.DESCRIPTION] &&
      currentStep[jsonKeyMapping.DESCRIPTION].trim()
    ) {
      return {
        title: currentStep[jsonKeyMapping.SLUG] || "Information",
        description: currentStep[jsonKeyMapping.DESCRIPTION],
      };
    }

    // Final fallback
    return {
      title: "Help",
      description: "No help information available for this step.",
    };
  };

  // Helper function to render text with bold formatting (same as ExpandableSidebar)
  const renderSeparatedContent = (text: string) => {
    // Split text by **bold** markers and render accordingly
    const parts = text.split(/(\*\*.*?\*\*)/g);
    const elements: JSX.Element[] = [];

    parts.forEach((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        // Remove the ** markers and render as bold outside subtitle container
        const boldText = part.slice(2, -2);
        elements.push(
          <b key={`bold-${index}`} className="force-bold">
            {boldText}
          </b>
        );
      } else if (part.trim()) {
        // Render regular text in subtitle container
        elements.push(
          <div
            key={`text-${index}`}
            className="subtitle"
            style={{ whiteSpace: "pre-line" }}
          >
            {part}
          </div>
        );
      }
    });

    return elements;
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // When tips dropdown is open, add a body class so global layout (nav + forms) shifts down
  useEffect(() => {
    if (isDropdownOpen) {
      document.body.classList.add("tips-dropdown-open");
    } else {
      document.body.classList.remove("tips-dropdown-open");
    }
    // Cleanup on unmount to avoid stale class
    return () => {
      document.body.classList.remove("tips-dropdown-open");
    };
  }, [isDropdownOpen]);

  // Use dynamic content if available, otherwise fall back to props
  const displayTitle = dynamicTitle || propTitle;
  const displayDescription =
    dynamicDescription ||
    propDescription ||
    "Help information will appear here.";

  return (
    <div className="horizontal-tips-container">
      {/* Tips Bar */}
      <div className={`tips-bar ${isDropdownOpen ? "dropdown-active" : ""}`}>
        <span className="tips-text">Tool & Tips</span>
        <button
          className={`tips-toggle ${isDropdownOpen ? "active" : ""}`}
          onClick={toggleDropdown}
          aria-label="Toggle tips menu"
        >
          <img
            src={`${process.env.PUBLIC_URL}/static/images/icon/tips.svg`}
            alt="Tips"
            className="tips-icon"
          />
        </button>

        <button
          id="toggleIconButton"
          className="toggle-icon-button"
          onClick={toggleDropdown}
          aria-label={isDropdownOpen ? "Collapse tips" : "Expand tips"}
        >
          {isDropdownOpen ? (
            // Minus icon
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 12H6"
                stroke="#16283D"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            // Plus icon
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 6V12M12 12V18M12 12H18M12 12L6 12"
                stroke="#16283D"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Tips Dropdown - 132px height, scrollable content */}
      <div className={`tool-tips-v1 ${isDropdownOpen ? "active" : ""}`}>
        {isDropdownOpen && (
          <div className="tips-scrollable-content">
            {displayTitle && (
              <div className="text">
                <b>{displayTitle}</b>
              </div>
            )}
            {renderSeparatedContent(displayDescription)}
          </div>
        )}
      </div>
    </div>
  );
};

export default HorizontalTipsBar;
